import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { characterLines, districts, promises } from './content/siteContent'
import { useAudio } from './hooks/useAudio'
import { useInView, useRenderQuality } from './hooks/useExperience'
import { normalizeAppStoreUrl } from './lib/experience'
import { trackEvent } from './lib/tracking'
import './styles/app.css'

const WorldScene = lazy(() => import('./scene/WorldScene'))
const CharacterScene = lazy(() => import('./scene/CharacterScene'))

const appStoreUrl = normalizeAppStoreUrl(import.meta.env.VITE_APP_STORE_URL)
const audioAvailable = import.meta.env.VITE_AUDIO_ENABLED === 'true'

function ArrowIcon() {
  return <span aria-hidden="true">↗</span>
}

function SparkIcon() {
  return <span aria-hidden="true">✦</span>
}

export default function App() {
  const [awake, setAwake] = useState(false)
  const [route, setRoute] = useState<'predicted' | 'living'>('predicted')
  const [systems, setSystems] = useState(() => new Set<string>())
  const [character, setCharacter] = useState(1)
  const [district, setDistrict] = useState(0)
  const [voiceCaptionVisible, setVoiceCaptionVisible] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const voiceCaptionTimer = useRef<number | null>(null)
  const quality = useRenderQuality()
  const characters = useInView<HTMLDivElement>()
  const audio = useAudio(audioAvailable)

  useEffect(() => {
    const elements = [...document.querySelectorAll<HTMLElement>('[data-reveal]')]
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.target.classList.toggle('is-visible', entry.isIntersecting)),
      { threshold: 0.14 },
    )
    elements.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!audio.consented) setVoiceCaptionVisible(false)
    return () => {
      if (voiceCaptionTimer.current) window.clearTimeout(voiceCaptionTimer.current)
    }
  }, [audio.consented])

  const wake = () => {
    if (!awake) {
      trackEvent('hero_wake_started')
      setAwake(true)
      audio.play('wake')
      window.setTimeout(() => {
        trackEvent('hero_wake_completed')
        if (audio.play('voice')) {
          setVoiceCaptionVisible(true)
          if (voiceCaptionTimer.current) window.clearTimeout(voiceCaptionTimer.current)
          voiceCaptionTimer.current = window.setTimeout(() => setVoiceCaptionVisible(false), 6500)
        }
      }, quality.reducedMotion ? 50 : 1100)
    }
  }

  const openCta = () => {
    if (appStoreUrl) {
      trackEvent('cta_app_store_clicked')
      window.location.assign(appStoreUrl)
      return
    }
    trackEvent('cta_coming_soon_opened')
    dialogRef.current?.showModal()
  }

  const chooseRoute = (next: 'predicted' | 'living') => {
    setRoute(next)
    trackEvent('route_choice_changed')
    if (next === 'living') audio.play('route')
  }

  const toggleSystem = (name: string) => {
    setSystems((current) => {
      const next = new Set(current)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
    trackEvent('system_layer_activated')
    audio.play(name === 'Living' ? 'bloom' : 'route')
  }

  const selectedDistrict = districts[district]
  const allSystemsAwake = systems.size === 3
  const appCta = appStoreUrl ? 'Download on the App Store' : 'Be first to play'

  return (
    <>
      <a className="skip-link" href="#story">Skip to story</a>

      <header className="site-header" aria-label="Primary navigation">
        <a className="brand" href="#top" aria-label="Worlds Awake home">
          <img src="/art/worlds-awake-wordmark-v1.webp" alt="Worlds Awake" width="164" height="82" />
        </a>
        <nav aria-label="Story sections">
          <a href="#story">Story</a>
          <a href="#play">How it plays</a>
          <a href="#promise">For everyone</a>
        </nav>
        <div className="header-actions">
          {audioAvailable && (
            <button
              className="sound-button"
              type="button"
              aria-label={audio.consented ? 'Sound on' : 'Sound off'}
              aria-pressed={audio.consented}
              onClick={() => void audio.setSound(!audio.consented)}
            >
              <span aria-hidden="true">{audio.consented ? '◖))' : '◖×'}</span>
              <span className="sound-label">{audio.consented ? 'Sound on' : 'Sound off'}</span>
            </button>
          )}
          <button className="button button-small" type="button" onClick={openCta}>{appCta}</button>
        </div>
      </header>

      <main id="top">
        <section className={`hero ${awake ? 'is-awake' : ''}`} aria-labelledby="hero-title">
          <div className="hero-art" aria-hidden="true">
            <img className="hero-fallback" src="/art/lumina-awakening-panorama-v1.webp" alt="" width="1672" height="941" fetchPriority="high" />
            {quality.webglAvailable && quality.tier !== 'still' && (
              <Suspense fallback={null}>
                <WorldScene
                  awake={awake}
                  reducedMotion={quality.reducedMotion}
                  dpr={quality.dpr}
                  particles={quality.particles}
                  shadows={quality.shadows}
                  onWake={wake}
                />
              </Suspense>
            )}
          </div>
          <div className="hero-wash" aria-hidden="true" />
          <div className="hero-content shell">
            <div className="hero-copy">
              <p className="eyebrow"><SparkIcon /> A world is waiting</p>
              <h1 id="hero-title">Wake what wonder can become.</h1>
              <p className="hero-deck">Build bright little kingdoms. Reconnect rivers, light, and life. Then watch every thoughtful choice transform a world in your hands.</p>
              <div className="button-row">
                <button className="button button-primary" type="button" onClick={openCta}>
                  {appCta} <ArrowIcon />
                </button>
                <button className="button button-ghost" type="button" onClick={wake}>
                  <SparkIcon /> {awake ? 'Lumina is awake' : 'Wake Lumina'}
                </button>
              </div>
              <p className="trust-line">A generous puzzle adventure for iPhone &amp; iPad <span aria-hidden="true">·</span> Ages 8+</p>
            </div>
            <button className="world-heart-target" type="button" onClick={wake} aria-label={awake ? 'Lumina is awake' : 'Touch the World Heart to wake Lumina'}>
              <span className="heart-pulse" aria-hidden="true" />
              <span>{awake ? 'Awake' : 'Touch to wake'}</span>
            </button>
            {voiceCaptionVisible && <p className="voice-caption" role="status">Lumina remembers the path. It only needs somewhere new to go.</p>}
          </div>
          <div className="scroll-cue" aria-hidden="true"><span /> Scroll to follow the light</div>
        </section>

        <section className="chapter stillness" id="story" aria-labelledby="stillness-title">
          <div className="chapter-orb" aria-hidden="true" />
          <div className="shell story-grid">
            <div className="story-copy" data-reveal>
              <p className="eyebrow">The Stillness</p>
              <h2 id="stillness-title">The Quiet Engine kept every world safe. <em>Too safe.</em></h2>
              <p>It removed every surprise, straightened every path, and repeated every answer. Nothing broke. Nothing grew. The floating worlds became perfectly stable—and completely still.</p>
            </div>
            <div className={`route-demo ${route === 'living' ? 'living' : ''}`} data-reveal aria-label="Choose a route through Lumina">
              <div className="route-sky" aria-hidden="true">
                <span className="route-node node-a" />
                <span className="route-node node-b" />
                <span className="route-node node-c" />
                <span className="route-line route-straight" />
                <span className="route-line route-bend-a" />
                <span className="route-line route-bend-b" />
                <span className="route-flower flower-a">✿</span>
                <span className="route-flower flower-b">✿</span>
              </div>
              <div className="route-controls" role="group" aria-label="Route choice">
                <button type="button" aria-pressed={route === 'predicted'} onClick={() => chooseRoute('predicted')}>The predicted path</button>
                <button type="button" aria-pressed={route === 'living'} onClick={() => chooseRoute('living')}>A path of your own</button>
              </div>
              <p className="engine-caption"><span>QUIET ENGINE</span> {route === 'living' ? 'Variance detected. Stability remains within tolerance.' : 'The straight route was stable. I predicted you would repeat it.'}</p>
            </div>
          </div>
        </section>

        <section className="chapter wayfinder" id="play" aria-labelledby="wayfinder-title">
          <div className="shell">
            <div className="section-heading centered" data-reveal>
              <p className="eyebrow">You are the Wayfinder</p>
              <h2 id="wayfinder-title">There is more than one right way to wake a world.</h2>
              <p>Place a home. Bend a stream. Reconnect light, water, warmth, roots, paths, and gears. Each useful connection changes the world you can see, hear, and touch.</p>
            </div>
            <div className="system-stage" data-reveal>
              <div className={`system-world ${allSystemsAwake ? 'complete' : ''}`} aria-hidden="true">
                <div className="system-island" />
                <div className={`system-route route-light ${systems.has('Light') ? 'active' : ''}`} />
                <div className={`system-route route-water ${systems.has('Water') ? 'active' : ''}`} />
                <div className={`system-route route-living ${systems.has('Living') ? 'active' : ''}`} />
                <div className="system-crystal">✦</div>
                <div className="system-home">⌂</div>
                <div className="system-tree">♣</div>
              </div>
              <div className="system-controls" role="group" aria-label="Wake Lumina's connected systems">
                {[
                  ['Light', 'Place structures where they make sense to you.'],
                  ['Water', 'Trace living routes through every district.'],
                  ['Living', 'Bring Wonders—and their stories—back to life.'],
                ].map(([name, description], index) => (
                  <button key={name} type="button" className={systems.has(name) ? 'active' : ''} aria-pressed={systems.has(name)} onClick={() => toggleSystem(name)}>
                    <span className="system-index">0{index + 1}</span>
                    <span><strong>{name === 'Light' ? 'Build' : name === 'Water' ? 'Connect' : 'Restore'}</strong>{description}</span>
                    <span className="system-check" aria-hidden="true">{systems.has(name) ? '✓' : '+'}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="chapter characters" aria-labelledby="characters-title">
          <div className="shell character-grid">
            <div className="character-copy" data-reveal>
              <p className="eyebrow">Little lives, big reactions</p>
              <h2 id="characters-title">The characters notice everything.</h2>
              <p>Warm a window and someone comes home. Repair a bridge and new neighbors meet halfway. Wake a garden and the whole district finds a reason to celebrate.</p>
              <div className="character-tabs" role="group" aria-label="Meet Lumina's citizens">
                {characterLines.map((person, index) => (
                  <button key={person.name} type="button" aria-pressed={character === index} onClick={() => setCharacter(index)}>
                    <span className="character-dot" style={{ background: person.palette }} aria-hidden="true" />
                    <span><strong>{person.name}</strong>{person.role}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="character-stage" ref={characters.ref} data-reveal>
              {characters.visible && quality.webglAvailable && quality.tier !== 'still' ? (
                <Suspense fallback={<div className="character-fallback" aria-hidden="true"><img src={`/art/characters/${['mira-garden-keeper-v1', 'pip-light-tinkerer-v1', 'ori-sky-cartographer-v1'][character]}.webp`} alt="" /></div>}>
                  <CharacterScene selected={character} dpr={quality.dpr} shadows={quality.shadows} onSelect={setCharacter} />
                </Suspense>
              ) : (
                <div className="character-fallback" aria-hidden="true">
                  <img src={`/art/characters/${['mira-garden-keeper-v1', 'pip-light-tinkerer-v1', 'ori-sky-cartographer-v1'][character]}.webp`} alt="" />
                </div>
              )}
              <blockquote>
                “{characterLines[character].line}”
                <cite>— {characterLines[character].name}, {characterLines[character].role}</cite>
              </blockquote>
            </div>
          </div>
        </section>

        <section className="chapter panorama" aria-labelledby="dynamic-title">
          <div className="panorama-art" role="img" aria-label="A crystal World Heart wakes a floating garden island from night into sunrise." />
          <div className="panorama-card" data-reveal>
            <p className="eyebrow">A living puzzle</p>
            <h2 id="dynamic-title">Touch one system. Watch the whole world answer.</h2>
            <p>Routes wake buildings. Buildings create new possibilities. Wonders change the light, music, weather, and movement of an entire district.</p>
            <div className="proof-grid">
              <span><strong>A world that responds</strong>Water runs, lanterns glow, and gardens open.</span>
              <span><strong>Choices, not traps</strong>Multiple layouts work. Undo is welcome.</span>
              <span><strong>A challenge that fits</strong>Choose relaxed or brainy between attempts.</span>
              <span><strong>Ready wherever you are</strong>Designed to work offline after download.</span>
            </div>
          </div>
        </section>

        <section className="chapter intelligence" aria-labelledby="intelligence-title">
          <div className="intelligence-graphic" aria-hidden="true">
            <span className="intelligence-ring ring-one" />
            <span className="intelligence-ring ring-two" />
            <span className="intelligence-core">✦</span>
            <span className="intelligence-path path-one" />
            <span className="intelligence-path path-two" />
            <span className="intelligence-path path-three" />
          </div>
          <div className="shell intelligence-copy" data-reveal>
            <p className="eyebrow">An intelligence learns to listen</p>
            <h2 id="intelligence-title">Can a machine protect change without controlling it?</h2>
            <p>The Quiet Engine can predict the safest path. Your choices show it that stability is not stillness—and that a living world can bend, recover, surprise itself, and choose what it becomes next.</p>
            <blockquote>“Protect what can change.”</blockquote>
            <details onToggle={(event) => {
              if (event.currentTarget.open) trackEvent('ai_disclosure_opened')
            }}>
              <summary>How the intelligence works</summary>
              <p><em>Worlds Awake</em> uses carefully authored levels and transparent systems. It does not send player conversations or personal data to a generative AI service during play. The Quiet Engine is a character in the story; the world’s dynamic response comes from connected, deterministic game systems.</p>
            </details>
          </div>
        </section>

        <section className="chapter promise" id="promise" aria-labelledby="promise-title">
          <div className="shell promise-grid">
            <div className="promise-copy" data-reveal>
              <p className="eyebrow">Wonder without pressure</p>
              <h2 id="promise-title">Big wonder. Gentle rules. Room for every kind of thinker.</h2>
              <p><em>Worlds Awake</em> is designed for players 8 and up—and for anyone who wants thoughtful play without combat, ads, energy timers, loot boxes, or failure designed to make you pay.</p>
            </div>
            <ul className="promise-list" data-reveal>
              {promises.map((promise) => <li key={promise}><span aria-hidden="true">✓</span>{promise}</li>)}
            </ul>
          </div>
        </section>

        <section className="chapter worlds" aria-labelledby="worlds-title">
          <div className="shell">
            <div className="section-heading" data-reveal>
              <p className="eyebrow">Eight awakenings in Lumina</p>
              <h2 id="worlds-title">Every district has something new to teach the light.</h2>
            </div>
            <div className="district-viewer" data-reveal style={{ '--district-a': selectedDistrict.colors[0], '--district-b': selectedDistrict.colors[1] } as React.CSSProperties}>
              <button className="district-arrow previous" type="button" aria-label="Previous district" onClick={() => {
                const next = (district - 1 + districts.length) % districts.length
                setDistrict(next)
                trackEvent('district_viewed')
              }}>←</button>
              <div className="district-island" aria-hidden="true">
                <span className="district-glow" />
                <span className="district-land" />
                <span className="district-wonder">✦</span>
              </div>
              <div className="district-copy" aria-live="polite">
                <span>{String(district + 1).padStart(2, '0')} / 08</span>
                <h3>{selectedDistrict.name}</h3>
                <p>{selectedDistrict.note}</p>
              </div>
              <button className="district-arrow next" type="button" aria-label="Next district" onClick={() => {
                const next = (district + 1) % districts.length
                setDistrict(next)
                trackEvent('district_viewed')
              }}>→</button>
              <div className="district-dots" role="group" aria-label="Choose a district">
                {districts.map((item, index) => (
                  <button key={item.name} type="button" aria-label={item.name} aria-pressed={district === index} onClick={() => {
                    setDistrict(index)
                    trackEvent('district_viewed')
                  }} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="finale" aria-labelledby="finale-title">
          <img src="/art/worlds-awake-social-v1.webp" alt="" width="1774" height="887" loading="lazy" />
          <div className="finale-wash" aria-hidden="true" />
          <div className="finale-content" data-reveal>
            <p className="eyebrow">Your world is waiting</p>
            <h2 id="finale-title">Lumina is ready for your first choice.</h2>
            <p>Wake eight handcrafted levels. Build a kingdom that remembers what you changed. Discover a story about safety, possibility, and making room for more than one right answer.</p>
            <div className="button-row centered-row">
              <button className="button button-primary" type="button" onClick={openCta}>{appStoreUrl ? 'Download on the App Store' : 'Join the wake list'} <ArrowIcon /></button>
              <a className="button button-ghost" href="#top" onClick={() => setAwake(false)}>Replay the awakening</a>
            </div>
          </div>
        </section>
      </main>

      <footer id="privacy">
        <div className="shell footer-grid">
          <div>
            <img src="/art/worlds-awake-wordmark-v1.webp" alt="Worlds Awake" width="180" height="90" loading="lazy" />
            <p>A bright kingdom-restoration adventure for iPhone and iPad.</p>
          </div>
          <div>
            <strong>Our promise</strong>
            <p>This preview uses no advertising trackers and collects no email address or personal profile. Anonymous, allowlisted interaction events may be recorded to improve the experience.</p>
          </div>
          <div className="footer-links">
            <a href="#story">Story</a>
            <a href="#play">How it plays</a>
            <a href="#promise">For everyone</a>
            <button type="button" onClick={openCta}>iOS availability</button>
          </div>
        </div>
        <div className="shell footer-bottom"><span>© 2026 Worlds Awake</span><span>Protect what can change.</span></div>
      </footer>

      <dialog ref={dialogRef} className="coming-soon" aria-labelledby="coming-soon-title">
        <form method="dialog">
          <button className="dialog-close" aria-label="Close">×</button>
          <span className="dialog-spark" aria-hidden="true">✦</span>
          <p className="eyebrow">Coming to iPhone &amp; iPad</p>
          <h2 id="coming-soon-title">The App Store page is waking up.</h2>
          <p>Worlds Awake is being prepared for its first players. We will activate the official download link here as soon as the public App Store listing is ready.</p>
          <button className="button button-primary" value="close">Keep exploring Lumina</button>
          <small>No email is collected by this preview.</small>
        </form>
      </dialog>
    </>
  )
}
